import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    CircularProgress,
    Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import TrainerWeeklyNotes from './TrainerWeeklyNotes';

const WeekPaper = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    overflow: 'hidden'
}));

const DaySection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: '#fff',
    height: '100%'
}));

const ClassCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    '&:last-child': {
        marginBottom: 0
    }
}));

export default function TrainerScheduleMobile({ 
    affiliate, 
    trainers, 
    classes,
    visibleWeeks,
    getTrainerColor,
    onClassClick,
    isEditing,
    isEditingPayable,
    pendingChanges,
    pendingPayableChanges
}) {
    const [expandedWeek, setExpandedWeek] = useState(null);

    const getMonday = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(d.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    };

    const getWeekDateRange = (weekStart) => {
        const startDate = new Date(weekStart);
        const endDate = new Date(weekStart);
        endDate.setDate(endDate.getDate() + 6);
        
        return `${startDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short' 
        })} - ${endDate.toLocaleDateString('en-GB', { 
            day: '2-digit', 
            month: 'short',
            year: 'numeric'
        })}`;
    };

    const getDayName = (date) => {
        return date.toLocaleDateString('en-GB', { weekday: 'long' });
    };

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-GB', { 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    // Group classes by week and day
    const getWeekClasses = (weekStart) => {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        
        const weekClasses = classes.filter(classItem => {
            const classDate = new Date(classItem.time);
            return classDate >= weekStart && classDate < weekEnd;
        });

        // Group by day
        const classesByDay = {};
        for (let i = 0; i < 7; i++) {
            const dayDate = new Date(weekStart);
            dayDate.setDate(dayDate.getDate() + i);
            const dayKey = dayDate.toDateString();
            classesByDay[dayKey] = [];
        }

        weekClasses.forEach(classItem => {
            const classDate = new Date(classItem.time);
            const dayKey = classDate.toDateString();
            if (classesByDay[dayKey]) {
                classesByDay[dayKey].push(classItem);
            }
        });

        return classesByDay;
    };

    const handleAccordionChange = (weekKey) => (event, isExpanded) => {
        setExpandedWeek(isExpanded ? weekKey : null);
    };

    // Auto-expand current week
    useEffect(() => {
        if (visibleWeeks.length > 0 && !expandedWeek) {
            const today = new Date();
            const currentMonday = getMonday(today);
            const currentWeekKey = currentMonday.toISOString();
            
            // Check if current week is in visible weeks
            const hasCurrentWeek = visibleWeeks.some(week => 
                week.toISOString() === currentWeekKey
            );
            
            if (hasCurrentWeek) {
                setExpandedWeek(currentWeekKey);
            } else if (visibleWeeks.length > 0) {
                // Expand first week if current week not visible
                setExpandedWeek(visibleWeeks[0].toISOString());
            }
        }
    }, [visibleWeeks]);

    if (!visibleWeeks || visibleWeeks.length === 0) {
        return (
            <Box sx={{ textAlign: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {visibleWeeks.map((weekStart) => {
                const weekKey = weekStart.toISOString();
                const classesByDay = getWeekClasses(weekStart);
                const hasClasses = Object.values(classesByDay).some(dayClasses => dayClasses.length > 0);

                return (
                    <Accordion
                        key={weekKey}
                        expanded={expandedWeek === weekKey}
                        onChange={handleAccordionChange(weekKey)}
                        sx={{ mb: 2 }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: '#f5f5f5',
                                '&.Mui-expanded': {
                                    backgroundColor: '#e3f2fd'
                                }
                            }}
                        >
                            <Typography variant="subtitle1" fontWeight="bold">
                                {getWeekDateRange(weekStart)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                            {/* Notes Section */}
                            <Box sx={{ p: 2, backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
                                <TrainerWeeklyNotes
                                    affiliate={affiliate}
                                    trainers={trainers}
                                    visibleWeeks={[weekStart]}
                                    isMobile={true}
                                />
                            </Box>

                            {/* Days Section */}
                            {hasClasses ? (
                                <Box sx={{ p: 1.5 }}>
                                    <Grid container spacing={1.5}>
                                        {Object.entries(classesByDay).map(([dayKey, dayClasses]) => {
                                            if (dayClasses.length === 0) return null;

                                            const dayDate = new Date(dayKey);
                                            
                                            // Sort classes by time
                                            dayClasses.sort((a, b) => 
                                                new Date(a.time) - new Date(b.time)
                                            );

                                            return (
                                                <Grid item xs={6} key={dayKey}>
                                                    <DaySection>
                                                        <Typography 
                                                            variant="subtitle2" 
                                                            fontWeight="bold" 
                                                            sx={{ mb: 1, color: '#1976d2', fontSize: '0.8rem' }}
                                                        >
                                                            {getDayName(dayDate)}
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ display: 'block', mb: 1, color: '#666' }}
                                                        >
                                                            {dayDate.toLocaleDateString('en-GB', { 
                                                                day: '2-digit', 
                                                                month: 'short' 
                                                            })}
                                                        </Typography>

                                                        <Stack spacing={0.5}>
                                                            {dayClasses.map((classItem) => {
                                                                const classTime = new Date(classItem.time);
                                                                const endTime = new Date(classTime.getTime() + classItem.duration * 60000);
                                                                
                                                                const pendingTrainer = pendingChanges?.get(classItem.id);
                                                                const trainerName = pendingTrainer || classItem.trainer || 'Unassigned';
                                                                
                                                                const pendingPayable = pendingPayableChanges?.get(classItem.id);
                                                                const isPayable = pendingPayable !== undefined ? pendingPayable : classItem.payable;
                                                                
                                                                const trainerColor = getTrainerColor(trainerName, trainers);
                                                                const backgroundColor = isPayable ? trainerColor.bg : '#f5f5f5';
                                                                const textColor = isPayable ? trainerColor.text : '#666666';

                                                                return (
                                                                    <ClassCard
                                                                        key={classItem.id}
                                                                        onClick={() => {
                                                                            if (onClassClick) {
                                                                                // Create event-like object to match FullCalendar's structure
                                                                                const eventInfo = {
                                                                                    event: {
                                                                                        id: classItem.id.toString()
                                                                                    }
                                                                                };
                                                                                onClassClick(eventInfo);
                                                                            }
                                                                        }}
                                                                        sx={{
                                                                            backgroundColor: backgroundColor,
                                                                            color: textColor,
                                                                            border: `1px solid ${backgroundColor || '#e0e0e0'}`,
                                                                            cursor: (isEditing || isEditingPayable) ? 'pointer' : 'default',
                                                                            '&:active': (isEditing || isEditingPayable) ? {
                                                                                opacity: 0.7
                                                                            } : {}
                                                                        }}
                                                                    >
                                                                        <Typography variant="caption" fontWeight="bold" sx={{ display: 'block' }}>
                                                                            {formatTime(classTime)} - {formatTime(endTime)}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                                                                            {classItem.trainingName}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.3, fontSize: '0.7rem' }}>
                                                                            {trainerName}
                                                                        </Typography>
                                                                        {classItem.location && (
                                                                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', opacity: 0.7 }}>
                                                                                {classItem.location}
                                                                            </Typography>
                                                                        )}
                                                                        {pendingChanges?.has(classItem.id) && (
                                                                            <Chip 
                                                                                label="Modified" 
                                                                                size="small" 
                                                                                color="warning" 
                                                                                sx={{ mt: 0.3, height: 16, fontSize: '0.6rem' }}
                                                                            />
                                                                        )}
                                                                    </ClassCard>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </DaySection>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            ) : (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No classes scheduled for this week
                                    </Typography>
                                </Box>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
}

